import { useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { TServicePartner } from "../../../../types";
import {
  AddEditServicePartner,
  GetSpecificServicePartner,
} from "../../../../utils/api.constant";
import { Collapse } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { requiredFieldValidator } from "../../../../utils/input.validator";

const { Panel } = Collapse;

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * This component represents a form for creating or editing a service partner.
 * It handles the loading of data, including fetching specific service partner details,
 * countries, states, regions, and location types. It also handles form submission,
 * displaying toast messages, and resetting form fields.
 */
export default function ServicePartnerForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  // Hook to handle on load operations
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchSpecificPartner(); // Fetch specific service partner if ID is provided
      setIsLoading(false);
    };

    fetchData(); // Call the fetch function
  }, []);

  // Handle on submit event
  const onFinish = async (values: TServicePartner) => {
    try {
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditServicePartner, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificPartner = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificServicePartner, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          form.setFieldsValue(response.data);
          // fetchCities();
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Resets the district field and fetches the list of cities based on the selected state
   */

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Service Partner`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter name"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  placeholder="Enter service partner name"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="partnerCode"
                label="Code"
                // rules={[{ required: true, message: "Please enter the code" }]}
              >
                <Input autoComplete="off" placeholder="Enter code" />
              </Form.Item>
            </Col>
            {/* <Col span={24}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  placeholder="Select status"
                  options={[
                    { label: "Active", value: 1 },
                    { label: "Inactive", value: 0 },
                  ]}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              {props.id ? "Update" : "Save"}
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                // form.resetFields();
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
